import React, { useRef } from 'react'
import ReactSelect, { components } from 'react-select'

import { ReactComponent as DownArrowIcon } from '../../assets/icons/down-arrow-icon.svg'
import { ReactComponent as ClearIcon } from '../../assets/icons/x-icon.svg'

import { colors } from '../../styles/constants'
import styled, { css } from 'styled-components'
import { ErrorMessage } from './Input'

const Label = styled.label`
  position: absolute;
  z-index: 1;
  top: -12px;
  left: 14px;
  margin: 0px;
  background-color: ${colors.white};
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: ${colors.gray.dark};
  padding: 4px;
  transition: all 0.2s ease;
  pointer-events: none;
`

const Container = styled.div`
  position:relative;
  width: 100%;

  ${props => props.hasError && css`
    ${ErrorMessage} {
      bottom: -18px;
    }

    ${Label} {
      color: ${colors.red.dark};
    }
  `}
`
const Select = styled(ReactSelect)`
  .react-select {

    &__menu-portal {
      z-index: 2;
    }
    &__control {
      align-items: flex-end;
      padding: 0 0 0 16px;
      min-height: 56px;
      border: 1px solid ${colors.gray.medium};
      border-radius: 8px;
      box-shadow: none;

      ${props => props.hasError && css`
        border: 2px solid ${colors.red.dark} !important;
        color: ${colors.gray.darker};

        ${ErrorMessage} {
          bottom: -18px;
        }
        .react-select__indicator {
          svg {
            path {
              fill: ${colors.red.dark};
            }
          }
        }
      `}

      &--is-focused {
        border-color: ${colors.gray.dark};
        .react-select__indicator {
          svg {
            path {
              fill: ${colors.gray.mediumDark};
            }
          }
        }
      }
      &--menu-is-open {
        border-radius: 8px 8px 0px 0px;
      }
      &:hover,
      &:active,
      &:focus {
        border-color: ${colors.gray.medium};
        box-shadow: none;
      }
    }
    &__placeholder {
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 23px;
      display: flex;
      align-items: center;
      color: ${colors.gray.medium};
      margin: 0;
    }
    &__input {
      padding: 0;
      margin: 0;
    }
    &__indicators {
      width: 64px;
      display: flex;
      justify-content: flex-end;
      margin-left: 8px;
      max-height: 56px;
      position: absolute;
      top: 0;
      right: 16px;
      bottom: 0;
      gap: 8px;
    }
    &__value {
      &-container {
        padding: 0 72px 0 0;
        margin: 12px 0;
        row-gap: 12px;
        column-gap: 8px;
        max-height: 192px;
        overflow: overlay;
        // firefox
        scrollbar-width: thin;
        scrollbar-color: ${colors.gray.medium} transparent;
        // chrome
        &::-webkit-scrollbar {
          width: 5px;
        }
        &::-webkit-scrollbar-thumb {
          background: ${colors.gray.medium};
          border-radius: 10px;
        }
        &::-webkit-scrollbar-thumb:hover {
          background: ${colors.gray.dark};
        }
      }
    }
    &__indicator {
      width: 24px;
      height: 100%;
      padding: 0;
      justify-content: center;
      align-items: center;
      svg {
        path {
          fill: ${colors.gray.medium};
        }
      }
      &-separator {
        display: none;
      }
    }
    &__multi-value {
      margin: 0;
      height: 32px;
      border-radius: 32px;
      padding: 0 12px;
      display: flex;
      align-items: center;
      &__label {
        padding: 0;
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 2px;
      }
      &__remove {
        margin-left: 8px;
        padding: 0;
        &:hover {
          background: transparent;
        }
      }
    }
    &__menu {
      margin: -1px 0;
      box-shadow: none;
      border: 1px solid ${colors.gray.medium};
      border-radius: 0 0 8px 8px;
      z-index: 2;
      &-notice--no-options {
        text-align: left;
      }
      &-list {
        padding: 0;
        max-height: 200px;
        scrollbar-width: thin;
        scrollbar-color: ${colors.gray.medium} transparent;
        &::-webkit-scrollbar {
          width: 5px;
        }
        &::-webkit-scrollbar-thumb {
          background: ${colors.gray.medium};
          border-radius: 10px;
        }
        &::-webkit-scrollbar-thumb:hover {
          background: ${colors.gray.dark};
        }
      }
    }
    &__option {
      font-family: 'Open Sans';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 14px;
      padding: 0;
      padding-left: 16px;
      height: 40px;
      display: flex;
      align-items: center;
      &--is-focused,
      &:active {
        background-color: ${colors.cyan.lighter};
      }
      &--is-selected {
        background-color: ${colors.cyan.medium};
      }
    }
  }
`

const SearchSelect = ({ label, placeholder, options, value, onChange, errorMessage, ...props }) => {
  const hasError = !!errorMessage
  const valueRef = useRef(value)
  valueRef.current = value

  const onChangeValue = (newValue, actionMeta) => {
    onChange(newValue || '', actionMeta)
  }

  const ClearIndicator = (props) => {
    return (
      <components.ClearIndicator {...props}>
        <ClearIcon />
      </components.ClearIndicator>
    )
  }

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownArrowIcon />
      </components.DropdownIndicator>
    )
  }

  return (
    <Container hasError={hasError}>
      {label && (<Label>{label}</Label>)}
      <Select
        classNamePrefix="react-select"
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChangeValue}
        components={{ ClearIndicator, DropdownIndicator }}
        menuPosition="fixed"
        hasError={hasError}
        {...props}
      />

      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  )
}

export default SearchSelect
